import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Bell, Droplets, Thermometer, Wind } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { userApiService } from "../services/userApiService";
import { subscribeUserDevices } from "../services/deviceService";

const metricIcon = { temp: Thermometer, humidity: Droplets, soil: Wind };

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      onClick={onChange}
      type="button"
      disabled={disabled}
      className={`relative h-7 w-12 rounded-full transition-colors disabled:opacity-50 ${checked ? "bg-accent" : "bg-muted"}`}
    >
      <span className={`absolute top-1 h-5 w-5 rounded-full bg-card shadow transition-all ${checked ? "left-6" : "left-1"}`} />
    </button>
  );
}

function metricUnit(metric) {
  const m = String(metric || "").toLowerCase();
  return m === "temp" || m === "temperature" ? "°C" : "%";
}

export default function UserAlertRulesEditor({ userId, compact = false, title = "Alert rules" }) {
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [rules, setRules] = useState([]);
  const [deviceOptions, setDeviceOptions] = useState([{ id: "All", name: "All devices" }]);

  const loadRules = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await userApiService.getAlertRules();
      setRules(Array.isArray(data?.items) ? data.items : []);
    } catch {
      toast.error("Failed to load alert rules");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  useEffect(() => {
    if (!userId) return;
    const unsub = subscribeUserDevices(userId, (devices) => {
      const options = [{ id: "All", name: "All devices" }];
      Object.entries(devices || {}).forEach(([id, meta]) => {
        options.push({ id, name: meta?.name || id });
      });
      setDeviceOptions(options);
    });
    return unsub;
  }, [userId]);

  const activeCount = useMemo(() => rules.filter((r) => r.enabled).length, [rules]);

  async function persistRule(templateId, patch) {
    setSavingId(templateId);
    try {
      await userApiService.upsertAlertRule(templateId, patch);
      setRules((prev) =>
        prev.map((r) => (r.templateId === templateId ? { ...r, ...patch, enabled: patch.enabled !== false } : r))
      );
    } catch {
      toast.error("Failed to save rule");
      await loadRules();
    } finally {
      setSavingId("");
    }
  }

  async function toggleRule(rule) {
    const nextEnabled = !rule.enabled;
    if (nextEnabled) {
      await persistRule(rule.templateId, {
        enabled: true,
        value: rule.value,
        channels: rule.channels,
        device: rule.device,
      });
      return;
    }
    await userApiService.deleteAlertRule(rule.templateId);
    setRules((prev) => prev.map((r) => (r.templateId === rule.templateId ? { ...r, enabled: false } : r)));
  }

  function updateLocalRule(templateId, field, value) {
    setRules((prev) => prev.map((r) => (r.templateId === templateId ? { ...r, [field]: value } : r)));
  }

  async function saveRuleCustomization(rule) {
    if (!rule.enabled) {
      toast.message("Enable the rule first");
      return;
    }
    await persistRule(rule.templateId, {
      enabled: true,
      value: Number(rule.value),
      channels: rule.channels,
      device: rule.device,
    });
    toast.success("Rule saved");
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading alert rules...</p>;
  }

  if (!rules.length) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground">
        No alert rule templates published yet. Ask an admin to add rules in the dashboard.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-2xl flex items-center gap-2">
            <Bell className="h-5 w-5 text-accent" />
            {title}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Admin-defined thresholds. Enable and customize how you want to be notified.
          </p>
        </div>
        <div className="text-sm text-muted-foreground">{activeCount} active</div>
      </div>

      <div className={`space-y-3 ${compact ? "" : ""}`}>
        {rules.map((rule) => {
          const Icon = metricIcon[rule.metric] || AlertTriangle;
          const isSaving = savingId === rule.templateId;
          const channelOptions = ["push", "email", "sms"];
          return (
            <div
              key={rule.templateId}
              className="rounded-2xl bg-card border border-border p-5 space-y-4 hover:shadow-soft transition-all"
            >
              <div className="flex items-start gap-4 flex-wrap">
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${rule.enabled ? "bg-accent/10 text-accent" : "bg-muted text-muted-foreground"}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <div className="font-display text-xl capitalize">
                    {rule.label || `When ${rule.metric} is ${rule.op} ${rule.defaults?.value ?? rule.value}`}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Template from admin · default {rule.defaults?.device || "All"} · {(rule.defaults?.channels || []).join(", ")}
                  </div>
                </div>
                <Toggle checked={!!rule.enabled} onChange={() => toggleRule(rule)} disabled={isSaving} />
              </div>

              {rule.enabled ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-border">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Threshold</label>
                    <input
                      type="number"
                      value={rule.value ?? ""}
                      onChange={(e) => updateLocalRule(rule.templateId, "value", e.target.value)}
                      className="mt-2 w-full h-11 px-4 rounded-xl bg-secondary/50 border border-border focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                    <div className="text-[10px] text-muted-foreground mt-1">{metricUnit(rule.metric)} · {rule.op}</div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Device scope</label>
                    <select
                      value={rule.device || "All"}
                      onChange={(e) => updateLocalRule(rule.templateId, "device", e.target.value)}
                      className="mt-2 w-full h-11 px-4 rounded-xl bg-secondary/50 border border-border focus:outline-none focus:ring-2 focus:ring-accent"
                    >
                      {deviceOptions.map((d) => (
                        <option key={d.id} value={d.id === "All" ? "All" : d.name}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Notify via</label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {channelOptions.map((ch) => {
                        const checked = (rule.channels || []).includes(ch);
                        return (
                          <label key={ch} className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                const next = checked
                                  ? (rule.channels || []).filter((c) => c !== ch)
                                  : [...(rule.channels || []), ch];
                                updateLocalRule(rule.templateId, "channels", next.length ? next : ["push"]);
                              }}
                            />
                            {ch}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div className="sm:col-span-2 lg:col-span-4">
                    <Button size="sm" variant="outline" disabled={isSaving} onClick={() => saveRuleCustomization(rule)}>
                      Save customization
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
